import * as canisterService from './canisterService'
import * as serverService from './serverService'
import { ServiceProvider, ServiceWrapper, ProviderResolutionCache } from '../types/dataManager'

// Service instance cache
const serviceCache = new Map<ServiceProvider, ServiceWrapper>()
const providerCache: ProviderResolutionCache = {
  assetProviders: new Map(),
  projectProviders: new Map(),
  lastResolved: new Map()
}

const CACHE_TIMEOUT = 5 * 60 * 1000 // 5 minutes

// Create a wrapped service instance
function createServiceWrapper(provider: ServiceProvider): ServiceWrapper {
  if (provider === 'not-set') {
    console.debug('🚫 Attempted to create service wrapper for not-set provider - this should not happen in normal flow')
    throw new Error('Service wrapper cannot be created - provider not initialized')
  }
  
  const service = provider === 'canister' ? canisterService : serverService
  console.debug(`🏭 Creating service wrapper for ${provider} provider`)
  console.debug(`🏭 Selected service is:`, service === canisterService ? 'canisterService' : 'serverService')
  
  return {
    // Common methods (properly bound)
    getUUIDInfo: (uuid: string) => {
      return service.getUUIDInfo(uuid)
    },
    getProjectByUuid: (uuid: string) => {
      return service.getProjectByUuid(uuid)
    },
    getProject: (projectUuid: string) => {
      return service.getProject(projectUuid)
    },
    getAssetCore: (uuid: string) => {
      return service.getAssetCore(uuid)
    },
    isFileId: (value: string) => service.isFileId(value),
    getFileIcon: (mimeType: string) => service.getFileIcon(mimeType),
    
    // Methods that might not be available in all services (properly bound)
    updateValue: (uuid: string, key: string, value: string, lock?: boolean) => 
      service.updateValue(uuid, key, value, lock),
    uploadFile: (uuid: string, fileData: string, metadata: { fileName: string; mimeType: string; uploadTimestamp: bigint }) => {
      return service.uploadFile(uuid, fileData, metadata)
    },
    getImage: (uuid: string, imageId: string) => service.getImage(uuid, imageId),
    getFileMetadataByUUIDAndId: (uuid: string, fileId: string) => {
      console.debug(`🔧 ServiceWrapper calling getFileMetadataByUUIDAndId with ${provider} provider`)
      console.debug(`🔧 Service object is:`, service === canisterService ? 'canisterService' : 'serverService')
      return service.getFileMetadataByUUIDAndId(uuid, fileId)
    },
    getFileMetadataByUUID: (uuid: string) => service.getFileMetadataByUUID(uuid),
    getFileByUUIDAndId: (uuid: string, fileId: string) => 
      service.getFileByUUIDAndId(uuid, fileId),
    downloadFileContent: (uuid: string, fileId: string) => {
      return service.downloadFileContent(uuid, fileId)
    },
    checkInstallerProjectPermission: (serialNo: string) => 
      service.checkInstallerProjectPermission(serialNo)
  }
}

// Get cached service instance
export function getServiceInstance(provider: ServiceProvider): ServiceWrapper {
  console.debug(`🏪 Getting service instance for provider: ${provider}`)
  console.debug(`🏪 Cache has ${provider}:`, serviceCache.has(provider))
  
  if (!serviceCache.has(provider)) {
    console.debug(`🏪 Creating new wrapper for ${provider}`)
    serviceCache.set(provider, createServiceWrapper(provider))
  } else {
    console.debug(`🏪 Using cached wrapper for ${provider}`)
  }
  
  const wrapper = serviceCache.get(provider)!
  console.debug(`🏪 Returning wrapper for ${provider}`)
  return wrapper
}

// Clear service cache (useful for testing or auth changes)
export function clearServiceCache(): void {
  serviceCache.clear()
}

// Check if provider resolution is cached and still valid
function isCacheValid(key: string, cacheMap: Map<string, number>): boolean {
  const lastResolved = cacheMap.get(key)
  if (!lastResolved) return false
  return Date.now() - lastResolved < CACHE_TIMEOUT
}

// Resolve provider for asset UUID with caching
export async function resolveAssetProvider(uuid: string): Promise<ServiceProvider> {
  // Check cache first
  if (isCacheValid(uuid, providerCache.lastResolved)) {
    const cachedProvider = providerCache.assetProviders.get(uuid)
    if (cachedProvider) {
      console.debug(`🏪 Service factory using cached provider ${cachedProvider} for UUID ${uuid}`)
      return cachedProvider
    }
  }

  console.debug(`🔬 Service factory resolving provider for UUID ${uuid}`)
  let resolvedProvider: ServiceProvider = 'canister' // default fallback

  try {
    // First try canister service - check if asset exists and is completed
    console.debug(`🔍 Checking canister for UUID ${uuid}`)
    const assetCore = await canisterService.getAssetCore(uuid)
    console.debug(`📋 Canister asset core for ${uuid}:`, assetCore)
    if (assetCore && assetCore.status === 'completed') {
      resolvedProvider = 'canister'
      console.debug(`✅ Using canister for ${uuid} (status: completed)`)
    } else {
      // If asset doesn't exist or isn't completed, try server
      resolvedProvider = 'server'
      console.debug(`🌐 Using server for ${uuid} (canister status: ${assetCore?.status || 'unknown'})`)
    }
  } catch (canisterError) {
    console.debug(`❌ Canister failed for ${uuid}:`, canisterError)
    try {
      // Try server service
      console.debug(`🔍 Checking server for UUID ${uuid}`)
      await serverService.getAssetCore(uuid)
      resolvedProvider = 'server'
      console.debug(`✅ Using server for ${uuid} (canister failed, server succeeded)`)
    } catch (serverError) {
      // If both fail, default to canister
      console.warn('Asset not found in either service, defaulting to canister:', { uuid, canisterError, serverError })
      resolvedProvider = 'not-set'
    }
  }

  if(resolvedProvider !== 'not-set') {
    // Cache the resolution
    providerCache.assetProviders.set(uuid, resolvedProvider)
    providerCache.lastResolved.set(uuid, Date.now())
  }else{
    return 'not-set'  //default
  }

  console.debug(`💾 Service factory cached provider ${resolvedProvider} for UUID ${uuid}`)
  return resolvedProvider
}

// Resolve provider for project ID with caching
export async function resolveProjectProvider(projectId: string): Promise<ServiceProvider> {
  // Check cache first
  const cacheKey = `project_${projectId}`
  if (isCacheValid(cacheKey, providerCache.lastResolved)) {
    const cachedProvider = providerCache.projectProviders.get(projectId)
    if (cachedProvider) {
      console.debug(`🏪 Service factory using cached provider ${cachedProvider} for project ${projectId}`)
      return cachedProvider
    }
  }

  console.debug(`🔬 Service factory resolving provider for project ${projectId}`)
  let resolvedProvider: ServiceProvider = 'not-set' // default to not-set

  try {
    // Try canister service first
    console.debug(`🔍 Checking canister for project ${projectId}`)
    await canisterService.getProject(projectId)
    resolvedProvider = 'canister'
    console.debug(`✅ Using canister for project ${projectId}`)
  } catch (canisterError) {
    console.debug(`❌ Canister failed for project ${projectId}:`, canisterError)
    try {
      // Try server service
      console.debug(`🔍 Checking server for project ${projectId}`)
      await serverService.getProject(projectId)
      resolvedProvider = 'server'
      console.debug(`✅ Using server for project ${projectId}`)
    } catch (serverError) {
      console.debug(`❌ Server failed for project ${projectId}:`, serverError)
      // If both fail, try getProjectByUuid in case projectId is actually a UUID
      try {
        console.debug(`🔍 Checking canister getProjectByUuid for ${projectId}`)
        await canisterService.getProjectByUuid(projectId)
        resolvedProvider = 'canister'
        console.debug(`✅ Using canister for project ${projectId} (via getProjectByUuid)`)
      } catch (canisterUuidError) {
        console.debug(`❌ Canister getProjectByUuid failed for ${projectId}:`, canisterUuidError)
        try {
          console.debug(`🔍 Checking server getProjectByUuid for ${projectId}`)
          await serverService.getProjectByUuid(projectId)
          resolvedProvider = 'server'
          console.debug(`✅ Using server for project ${projectId} (via getProjectByUuid)`)
        } catch (serverUuidError) {
          console.warn('Project not found in either service:', { projectId, canisterError, serverError, canisterUuidError, serverUuidError })
          resolvedProvider = 'not-set'
        }
      }
    }
  }

  if (resolvedProvider !== 'not-set') {
    // Cache the resolution only if we found a valid provider
    providerCache.projectProviders.set(projectId, resolvedProvider)
    providerCache.lastResolved.set(cacheKey, Date.now())
    console.debug(`💾 Service factory cached provider ${resolvedProvider} for project ${projectId}`)
  }

  return resolvedProvider
}

// Find which provider has a project that contains the given asset UUID
export async function resolveProjectFromAssetProvider(uuid: string): Promise<ServiceProvider> {
  // Check cache first
  const cacheKey = `asset_to_project_${uuid}`
  if (isCacheValid(cacheKey, providerCache.lastResolved)) {
    const cachedProvider = providerCache.assetProviders.get(`${uuid}_project`)
    if (cachedProvider) {
      console.debug(`🏪 Service factory using cached provider ${cachedProvider} for project from asset ${uuid}`)
      return cachedProvider
    }
  }

  console.debug(`🔬 Service factory resolving provider for project from asset ${uuid}`)
  let resolvedProvider: ServiceProvider = 'not-set' // default to not-set

  try {
    // Try canister service first
    console.debug(`🔍 Checking canister for project by UUID ${uuid}`)
    await canisterService.getProjectByUuid(uuid)
    resolvedProvider = 'canister'
    console.debug(`✅ Using canister for project from asset ${uuid}`)
  } catch (canisterError) {
    console.debug(`❌ Canister failed for project by UUID ${uuid}:`, canisterError)
    try {
      // Try server service
      console.debug(`🔍 Checking server for project by UUID ${uuid}`)
      await serverService.getProjectByUuid(uuid)
      resolvedProvider = 'server'
      console.debug(`✅ Using server for project from asset ${uuid}`)
    } catch (serverError) {
      console.warn('Project by asset UUID not found in either service:', { uuid, canisterError, serverError })
      resolvedProvider = 'not-set'
    }
  }

  if (resolvedProvider !== 'not-set') {
    // Cache the resolution only if we found a valid provider
    providerCache.assetProviders.set(`${uuid}_project`, resolvedProvider)
    providerCache.lastResolved.set(cacheKey, Date.now())
    console.debug(`💾 Service factory cached provider ${resolvedProvider} for project from asset ${uuid}`)
  }

  return resolvedProvider
}

// Clear provider cache (useful for testing or when providers change)
export function clearProviderCache(): void {
  providerCache.assetProviders.clear()
  providerCache.projectProviders.clear()
  providerCache.lastResolved.clear()
}

// Get cached provider if available
export function getCachedProvider(uuid: string, type: 'asset' | 'project'): ServiceProvider | null {
  const cacheKey = type === 'asset' ? uuid : `project_${uuid}`
  if (isCacheValid(cacheKey, providerCache.lastResolved)) {
    return type === 'asset' 
      ? providerCache.assetProviders.get(uuid) || null
      : providerCache.projectProviders.get(uuid) || null
  }
  return null
}